import { useCallback, useMemo } from "react";

import type { ISwSettings } from "app/@types/ISwSettings";
import { getPackageHref } from "app/common/hrefs";
import { swSettings } from "app/common/services/swSettings";
import { useFitScoreContext } from "app/contexts/FitScoreContext/FitScoreContext";
import { isInCampaignTime } from "app/marketing/functions/isInCampaignTime";
import type { PackageName } from "app/common/constants/packageNames";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import { hasAddonsEnabled, isMonthlyPaymentPeriod, isSI } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { SEASONAL_PROMO_CODES_MAP, SEASONAL_DISCOUNT_UPGRADE_MAP } from "./useSeasonalCampaignTypes";

interface IUseSeasonalCampaignApi {
    promoCode?: string;
    isUpgradedToAnnual: boolean;
    isSeasonalCampaignActive: boolean;
    discountEndDate: string;
    handleCtaClick: () => void;
}

export const useSeasonalCampaign = (): IUseSeasonalCampaignApi => {
    const discountEndDate = GrowthbookService.growthbookInstance.getFeatureValue(
        TestsNames.SEASONAL_DISCOUNTS,
        "2026-01-01",
    );

    const { hasMarketingGreenlight } = useFitScoreContext();

    const plan = swSettings.user.plan as PackageName;

    const promoCode = SEASONAL_PROMO_CODES_MAP[plan];

    // Note: In this instance this is true, but in later campaigns
    // we might upgrade package without annual update
    const isUpgradedToAnnual = useMemo(() => {
        return isMonthlyPaymentPeriod(swSettings as ISwSettings);
    }, []);

    const isSeasonalCampaignActive = useMemo(() => {
        return (
            hasMarketingGreenlight &&
            !!promoCode &&
            !isSI(swSettings) &&
            hasAddonsEnabled(swSettings) &&
            isInCampaignTime(discountEndDate)
        );
    }, [hasMarketingGreenlight, promoCode, discountEndDate]);

    const targetPackage = useMemo(() => {
        if (!promoCode || !(promoCode in SEASONAL_DISCOUNT_UPGRADE_MAP)) {
            return undefined;
        }

        const upgradesByPlan = SEASONAL_DISCOUNT_UPGRADE_MAP[promoCode as keyof typeof SEASONAL_DISCOUNT_UPGRADE_MAP];

        if (!(plan in upgradesByPlan)) {
            return undefined;
        }

        return upgradesByPlan[plan as keyof typeof upgradesByPlan];
    }, [promoCode, plan]);

    const handleCtaClick = useCallback(() => {
        const packageHref = getPackageHref();
        const url = new URL(packageHref, window.location.origin);
        url.searchParams.set("promo", promoCode ?? "");

        if (isUpgradedToAnnual) {
            url.searchParams.set("withUpgradeToAnnual", "true");
        }

        if (targetPackage) {
            url.searchParams.set("upgradeTo", targetPackage);
        }

        window.open(url.toString());
    }, [promoCode, isUpgradedToAnnual, targetPackage]);

    return {
        promoCode,
        isSeasonalCampaignActive,
        isUpgradedToAnnual,
        discountEndDate,
        handleCtaClick,
    };
};
